// CHA-587 (W6) — Agent configuration: see and edit each agent's model, tools, skills, memory and
// approval policy. Config only — nothing here calls a model, and runs do not read it yet.
//
// Platform and tenant-agnostic: the shell passes `call(payload)`, which runs a gateway action for its
// tenant and resolves to the callable's data. Every write goes through upsert_agent, which validates
// the fields and enforces the admin role; the model and approval choices come from the server.
import React, { useCallback, useState } from 'react';

const APPROVAL_LABELS = {
  coo: 'COO decides',
  agent_with_approval: 'Agent proposes, COO approves',
  agent: 'Agent acts on its own',
};

const inputCls = 'w-full border border-carbon-border bg-carbon-white px-2 py-1 text-[12px] text-carbon-ink';
const labelCls = 'mb-0.5 block font-plex-mono text-[10px] uppercase tracking-[0.08em] text-carbon-gray-60';
const btnCls = 'font-plex-mono text-[10px] uppercase tracking-[0.08em] text-carbon-gray-70 hover:text-carbon-ink disabled:opacity-40';

// "a, b ,a" -> ['a', 'b'] (the server checks each entry is a slug).
const toList = (text) => [...new Set(String(text || '').split(',').map((s) => s.trim()).filter(Boolean))];

export default function AgentConfigPanel({ call }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [agents, setAgents] = useState([]);
  const [models, setModels] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await call({ action: 'list_agents' });
      setAgents(Array.isArray(res?.agents) ? res.agents : []);
      setModels(Array.isArray(res?.models) ? res.models : []);
      setApprovals(Array.isArray(res?.approvals) ? res.approvals : []);
    } catch (err) {
      setError(err?.message || 'Could not load agent configuration.');
    } finally {
      setLoading(false);
    }
  }, [call]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) load();
    else setForm(null);
  };

  const edit = (a) => setForm({
    id: a.id,
    name: a.name || '',
    model: a.model || '',
    approval: a.approval || '',
    tools: (a.tools || []).join(', '),
    skills: (a.skills || []).join(', '),
    memoryRef: a.memoryRef || '',
  });

  const save = async (e) => {
    e.preventDefault();
    if (!form) return;
    setSaving(true);
    setError(null);
    try {
      await call({
        action: 'upsert_agent',
        agent: {
          id: form.id,
          name: form.name,
          model: form.model,
          approval: form.approval,
          tools: toList(form.tools),
          skills: toList(form.skills),
          memoryRef: form.memoryRef.trim() || null,
        },
      });
      setForm(null);
      await load();
    } catch (err) {
      setError(err?.message || 'Could not save the agent configuration.');
    } finally {
      setSaving(false);
    }
  };

  const field = (key) => ({
    value: form[key],
    onChange: (ev) => setForm((f) => ({ ...f, [key]: ev.target.value })),
  });

  return (
    <section className="border border-carbon-border bg-carbon-white" data-testid="agent-config-panel" aria-label="Agent configuration">
      <div className="flex items-center justify-between border-b border-carbon-border bg-carbon-gray-10 px-4 py-2">
        <span className="font-plex-mono text-[11px] uppercase tracking-[0.12em] text-carbon-gray-60">
          Agent configuration{open ? ` · ${agents.length}` : ''}
        </span>
        <button type="button" onClick={toggle} className={btnCls}>{open ? 'Hide' : 'Show'}</button>
      </div>
      {open && (
        <div className="space-y-3 px-4 py-3">
          <p className="text-[11px] text-carbon-gray-60">Configuration only: agents do not act on these settings yet.</p>
          {error && <div role="alert" className="text-sm text-carbon-red-60">{error}</div>}
          {loading && <div className="text-sm text-carbon-gray-60">Loading agents…</div>}
          {!loading && agents.length === 0 && !error && (
            <div className="text-sm text-carbon-gray-60">No agents are configured for this tenant yet.</div>
          )}
          {!loading && agents.length > 0 && (
            <table className="w-full text-left text-[12px]" data-testid="agent-config-table">
              <thead>
                <tr className="text-carbon-gray-60">
                  <th className="py-1 font-normal">Agent</th><th className="font-normal">Model</th>
                  <th className="font-normal">Approval</th><th className="font-normal">Skills</th>
                  <th className="font-normal">Tools</th><th className="font-normal">Memory</th>
                  <th className="font-normal">Version</th><th />
                </tr>
              </thead>
              <tbody>
                {agents.map((a) => (
                  <tr key={a.id} data-testid={`agent-${a.id}`} className="border-t border-carbon-border">
                    <td className="py-1 text-carbon-ink">{a.name}</td>
                    <td className="font-plex-mono">{a.model}</td>
                    <td>{APPROVAL_LABELS[a.approval] || a.approval}</td>
                    <td className="font-plex-mono">{(a.skills || []).join(', ') || '—'}</td>
                    <td className="font-plex-mono">{(a.tools || []).join(', ') || '—'}</td>
                    <td className="max-w-[12rem] truncate">{a.memoryRef || '—'}</td>
                    <td>{a.version ?? '—'}</td>
                    <td className="text-right"><button type="button" className={btnCls} onClick={() => edit(a)} disabled={saving}>Edit</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {form && (
            <form onSubmit={save} className="grid grid-cols-1 gap-2 border border-carbon-border p-3 sm:grid-cols-2" data-testid="agent-config-form">
              <label><span className={labelCls}>Name</span><input className={inputCls} aria-label="Name" {...field('name')} required /></label>
              <label>
                <span className={labelCls}>Model</span>
                <select className={inputCls} aria-label="Model" {...field('model')} required>
                  {models.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </label>
              <label>
                <span className={labelCls}>Approval</span>
                <select className={inputCls} aria-label="Approval" {...field('approval')} required>
                  {approvals.map((a) => <option key={a} value={a}>{APPROVAL_LABELS[a] || a}</option>)}
                </select>
              </label>
              <label><span className={labelCls}>Memory reference</span><input className={inputCls} aria-label="Memory reference" {...field('memoryRef')} /></label>
              <label><span className={labelCls}>Skills (comma-separated)</span><input className={inputCls} aria-label="Skills" {...field('skills')} /></label>
              <label><span className={labelCls}>Tools (comma-separated)</span><input className={inputCls} aria-label="Tools" {...field('tools')} /></label>
              <div className="flex gap-4 sm:col-span-2">
                <button type="submit" className={btnCls} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
                <button type="button" className={btnCls} onClick={() => setForm(null)} disabled={saving}>Cancel</button>
              </div>
            </form>
          )}
        </div>
      )}
    </section>
  );
}
